// OpeningsPane
import React, { useMemo, useState } from "react";
import { uid, num, buildDoorCatalog, openingLinkId, shortId, wallCode } from "../../../helpers/rooms.helpers";

export default function OpeningsPane({
    title,
    items,
    onAdd,
    onChange,
    onRemove,
    kind = "doors",
    allRooms,
    selfRi,
    selfWi,
    onAddDoorGlobal,
    onUpdateOpeningGlobal,
    onRemoveOpeningGlobal,
    onUpdateOpeningsBatch,
    onRemoveOpeningsBatch,
}) {
    const [panel, setPanel] = useState({ oi: null, mode: null });

    const doorCatalog = useMemo(() => {
        if (kind !== "doors") return [];
        return buildDoorCatalog(allRooms);
    }, [allRooms, kind]);

    const catalogMap = useMemo(() => {
        const m = {};
        doorCatalog.forEach((x) => {
            m[x.id] = x;
        });
        return m;
    }, [doorCatalog]);

    const wallsCatalog = useMemo(() => {
        if (kind !== "doors") return [];
        const R = Array.isArray(allRooms) ? allRooms : [];
        const out = [];
        R.forEach((r, ri) => {
            const walls = Array.isArray(r?.walls) ? r.walls : [];
            walls.forEach((w, wi) => {
                const code = String(w?.code || wallCode(ri, wi));
                const roomName = String(r?.name || `Комната ${ri + 1}`);
                const wallName = w?.name ? String(w.name) : `Стена ${code}`;
                out.push({
                    ri,
                    wi,
                    code,
                    title: `${code} — ${roomName} — ${wallName}`,
                });
            });
        });
        return out.sort((a, b) => (a.title || "").localeCompare(b.title || "", "ru"));
    }, [allRooms, kind]);

    const canGlobal =
        typeof onUpdateOpeningGlobal === "function" &&
        typeof onRemoveOpeningGlobal === "function" &&
        Array.isArray(allRooms) &&
        kind === "doors";

    const canBatch =
        typeof onUpdateOpeningsBatch === "function" &&
        typeof onRemoveOpeningsBatch === "function" &&
        canGlobal;

    const selfWallCode = String(wallCode(selfRi, selfWi));

    const applyCurrent = (oi, patch) => {
        if (typeof onChange === "function") onChange(oi, patch);
    };

    const applyGlobal = (ri, wi, oi, patch) => {
        if (typeof onUpdateOpeningGlobal === "function") onUpdateOpeningGlobal(ri, wi, "doors", oi, patch);
    };

    const removeGlobal = (ri, wi, oi) => {
        if (typeof onRemoveOpeningGlobal === "function") onRemoveOpeningGlobal(ri, wi, "doors", oi);
    };

    const applyDoorGroupPatch = (groupId, patch) => {
        if (!canGlobal || !groupId) return false;
        const entry = catalogMap[groupId];
        const refs = Array.isArray(entry?.refs) ? entry.refs : [];
        if (!refs.length) return false;

        if (canBatch) {
            onUpdateOpeningsBatch(
                refs.map((r) => ({
                    ri: r.ri,
                    wi: r.wi,
                    kind: "doors",
                    oi: r.oi,
                    patch,
                }))
            );
            return true;
        }

        refs.forEach((r) => applyGlobal(r.ri, r.wi, r.oi, patch));
        return true;
    };

    const getDoorGroupId = (d) => {
        const x = openingLinkId(d);
        if (x) return x;
        const id = String(d?.id || "");
        return id;
    };

    const linkMetaText = (d) => {
        const gid = openingLinkId(d);
        if (!gid) return "";
        const entry = catalogMap[gid];
        const codes = Array.isArray(entry?.codes) ? entry.codes : [];
        if (!codes.length) return `Связано: #${shortId(gid)}`;
        if (codes.length === 1) return `Связано: ${codes[0]} ↔ ?`;
        return `Связано: ${codes.join(" ↔ ")}`;
    };

    const doorNameMapByGroup = useMemo(() => {
        if (kind !== "doors") return {};
        const R = Array.isArray(allRooms) ? allRooms : [];
        const map = {};

        R.forEach((r, ri) => {
            const walls = Array.isArray(r?.walls) ? r.walls : [];
            walls.forEach((w, wi) => {
                const code = String(w?.code || wallCode(ri, wi));
                const doors = Array.isArray(w?.doors) ? w.doors : [];
                doors.forEach((d) => {
                    const gid = String(openingLinkId(d) || d?.id || "");
                    if (!gid) return;
                    const lbl = String(d?.label || "").trim();
                    if (!map[gid]) map[gid] = {};
                    if (lbl) map[gid][code] = lbl;
                });
            });
        });

        return map;
    }, [allRooms, kind]);

    const doorNamesText = (gid) => {
        if (!gid) return "";
        const entry = catalogMap[gid];
        const codes = Array.isArray(entry?.codes) ? entry.codes : [];
        const namesByCode = doorNameMapByGroup[gid] || {};

        const ordered = codes.length ? codes : Object.keys(namesByCode);
        if (!ordered.length) return "";

        const parts = ordered.map((c) => namesByCode[c] || "?");
        return `[${parts.join(" ↔ ")}]`;
    };

    const handleFieldChange = (oi, patch) => {
        const d = (Array.isArray(items) ? items : [])[oi];
        const gid = openingLinkId(d);

        if (kind === "doors" && gid && canGlobal) {
            applyDoorGroupPatch(gid, patch);
            return;
        }

        applyCurrent(oi, patch);
    };

    const handleRemove = (oi) => {
        if (kind !== "doors" || !canGlobal) {
            onRemove(oi);
            return;
        }

        const d = (Array.isArray(items) ? items : [])[oi];
        const gid = openingLinkId(d);

        if (!gid) {
            onRemove(oi);
            return;
        }

        const entry = catalogMap[gid];
        const refs = Array.isArray(entry?.refs) ? entry.refs : [];

        if (refs.length <= 1) {
            onRemove(oi);
            return;
        }

        if (canBatch) {
            onRemoveOpeningsBatch(
                refs.map((r) => ({
                    ri: r.ri,
                    wi: r.wi,
                    kind: "doors",
                    oi: r.oi,
                }))
            );
            return;
        }

        const byWall = {};
        refs.forEach((r) => {
            const k = `${r.ri}:${r.wi}`;
            if (!byWall[k]) byWall[k] = [];
            byWall[k].push(r);
        });

        Object.values(byWall).forEach((list) => {
            list.sort((a, b) => (b.oi || 0) - (a.oi || 0)).forEach((r) => removeGlobal(r.ri, r.wi, r.oi));
        });
    };

    const handleUnlinkEverywhere = (oi) => {
        const d = (Array.isArray(items) ? items : [])[oi];
        const gid = openingLinkId(d);
        const patch = { linkId: "", doorGroupId: "" };

        if (kind === "doors" && canGlobal && gid) {
            applyDoorGroupPatch(gid, patch);
            setPanel({ oi: null, mode: null });
            return;
        }

        applyCurrent(oi, patch);
        setPanel({ oi: null, mode: null });
    };

    const resolveGroupId = (pickedId) => {
        const entry = catalogMap[pickedId];
        const refs = Array.isArray(entry?.refs) ? entry.refs : [];
        if (!refs.length) return String(pickedId);

        const r0 = refs[0];
        const targetDoor = allRooms?.[r0.ri]?.walls?.[r0.wi]?.doors?.[r0.oi];
        const targetGid = openingLinkId(targetDoor);

        if (targetGid) return String(targetGid);

        if (refs.length <= 1) return uid();

        return String(pickedId);
    };

    const handleLinkToExisting = (oi, pickedGroupId) => {
        if (!pickedGroupId) return;

        const d = (Array.isArray(items) ? items : [])[oi] || {};
        const oldGid = openingLinkId(d) ? String(openingLinkId(d)) : "";

        const newGroupId = resolveGroupId(String(pickedGroupId));
        const patch = { doorGroupId: newGroupId, linkId: newGroupId };

        const seen = new Set();
        const updates = [];

        const pushRef = (ri, wi, oi2) => {
            const k = `${ri}:${wi}:${oi2}`;
            if (seen.has(k)) return;
            seen.add(k);
            updates.push({ ri, wi, kind: "doors", oi: oi2, patch });
        };

        const pushGroup = (gid) => {
            const entry = catalogMap[gid];
            const refs = Array.isArray(entry?.refs) ? entry.refs : [];
            refs.forEach((r) => pushRef(r.ri, r.wi, r.oi));
        };

        if (oldGid) pushGroup(oldGid);
        else pushRef(selfRi, selfWi, oi);

        pushGroup(String(pickedGroupId));

        if (canBatch) {
            onUpdateOpeningsBatch(updates);
            setPanel({ oi: null, mode: null });
            return;
        }

        updates.forEach((u) => applyGlobal(u.ri, u.wi, u.oi, u.patch));
        setPanel({ oi: null, mode: null });
    };

    const handleAddSecondSide = (oi, targetKey) => {
        if (kind !== "doors") return;
        if (!canGlobal) return;
        if (typeof onAddDoorGlobal !== "function") return;
        if (!targetKey) return;

        const [tri, twi] = String(targetKey).split(":").map((x) => Number(x));
        if (!Number.isFinite(tri) || !Number.isFinite(twi)) return;
        if (tri === selfRi && twi === selfWi) return;

        const d = (Array.isArray(items) ? items : [])[oi] || {};
        const groupId = getDoorGroupId(d);

        const already = catalogMap[groupId];
        const refs = Array.isArray(already?.refs) ? already.refs : [];
        const hasTarget = refs.some((r) => r.ri === tri && r.wi === twi);
        if (hasTarget) {
            setPanel({ oi: null, mode: null });
            return;
        }

        const label = String(d?.label || "Дверь");
        const widthMm = num(d?.widthMm);
        const heightMm = num(d?.heightMm);
        const qty = Math.max(0, num(d?.qty ?? 1));

        const selfPatch = { doorGroupId: groupId, linkId: groupId };
        applyGlobal(selfRi, selfWi, oi, selfPatch);

        onAddDoorGlobal(tri, twi, {
            doorGroupId: groupId,
            linkId: groupId,
            label,
            widthMm,
            heightMm,
            qty,
        });

        setPanel({ oi: null, mode: null });
    };

    const filteredDoorCatalogForRow = (oi) => {
        const d = (Array.isArray(items) ? items : [])[oi] || {};
        const selfGid = openingLinkId(d) ? String(openingLinkId(d)) : "";
        const selfId = String(d?.id || "");

        return (Array.isArray(doorCatalog) ? doorCatalog : []).filter((x) => {
            if (!x?.id) return false;

            if (selfGid && String(x.id) === selfGid) return false;

            if (!selfGid && selfId && String(x.id) === selfId) {
                const entry = catalogMap[selfId];
                const refs = Array.isArray(entry?.refs) ? entry.refs : [];
                if (refs.length <= 1) return false;
            }

            return true;
        });
    };

    const panelOpen = (oi, mode) => panel?.oi === oi && panel?.mode === mode;

    return (
        <div className="smeta-rooms__pane">
            <div className="smeta-rooms__pane-head">
                <div className="smeta-rooms__sub-title-sm">{title}</div>
                <button type="button" className="smeta-rooms__btn smeta-rooms__btn--small" onClick={onAdd}>
                    + {title.slice(0, -1)}
                </button>
            </div>

            {(Array.isArray(items) ? items : []).map((o, oi) => {
                const gid = openingLinkId(o);
                const linkText = kind === "doors" ? linkMetaText(o) : "";
                const namesText = gid ? doorNamesText(gid) : "";

                return (
                    <div key={o.id || oi} className="smeta-rooms__opening">
                        <div className="smeta-rooms__row smeta-rooms__row--opening">
                            <input
                                className="smeta-rooms__input"
                                value={o.label || ""}
                                onChange={(e) => handleFieldChange(oi, { label: e.target.value })}
                                placeholder="Название/тип"
                            />
                            <input
                                className="smeta-rooms__input smeta-rooms__input--sm"
                                value={o.qty ?? ""}
                                onChange={(e) => handleFieldChange(oi, { qty: num(e.target.value) })}
                                inputMode="numeric"
                                placeholder="шт"
                            />
                            <input
                                className="smeta-rooms__input smeta-rooms__input--sm"
                                value={o.widthMm ?? ""}
                                onChange={(e) => handleFieldChange(oi, { widthMm: num(e.target.value) })}
                                inputMode="numeric"
                                placeholder="шир, мм"
                            />
                            <input
                                className="smeta-rooms__input smeta-rooms__input--sm"
                                value={o.heightMm ?? ""}
                                onChange={(e) => handleFieldChange(oi, { heightMm: num(e.target.value) })}
                                inputMode="numeric"
                                placeholder="выс, мм"
                            />

                            {kind === "doors" ? (
                                <div className="smeta-rooms__opening-actions">
                                    <button
                                        type="button"
                                        className="smeta-rooms__btn smeta-rooms__btn--tiny"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setPanel(panelOpen(oi, "link") ? { oi: null, mode: null } : { oi, mode: "link" });
                                        }}
                                        title="Связать с уже существующей дверью (другая стена)"
                                    >
                                        Связать
                                    </button>
                                </div>
                            ) : null}

                            <button
                                type="button"
                                className="smeta-rooms__btn smeta-rooms__btn--tiny smeta-rooms__btn--danger"
                                onClick={() => handleRemove(oi)}
                            >
                                x
                            </button>
                        </div>

                        {kind === "doors" ? (
                            <div className="smeta-rooms__door-linkline">
                                <div className="smeta-rooms__door-linktext">
                                    {linkText ? (
                                        <>
                                            {linkText}{" "}
                                            {namesText ? <b className="smeta-rooms__door-codes">{namesText}</b> : null}
                                        </>
                                    ) : (
                                        <>
                                            Связано: <b>{selfWallCode}</b> ↔ <b>?</b>
                                        </>
                                    )}
                                </div>

                                {gid ? (
                                    <button
                                        type="button"
                                        className="smeta-rooms__btn smeta-rooms__btn--tiny smeta-rooms__btn--neutral"
                                        onClick={() => handleUnlinkEverywhere(oi)}
                                        title="Разорвать связь у этой двери (снимет связь с обеих сторон)"
                                    >
                                        Разорвать
                                    </button>
                                ) : null}
                            </div>
                        ) : null}

                        {kind === "doors" && panelOpen(oi, "link") ? (
                            <div
                                className="smeta-rooms__door-panel"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                }}
                            >
                                <div className="smeta-rooms__door-panel-title">Связать с уже существующей дверью (другая стена)</div>

                                <div className="smeta-rooms__door-panel-row">
                                    <select
                                        className="smeta-rooms__select"
                                        defaultValue=""
                                        onChange={(e) => handleLinkToExisting(oi, e.target.value)}
                                    >
                                        <option value="" disabled>
                                            Выбрать дверь...
                                        </option>
                                        {filteredDoorCatalogForRow(oi).map((d) => (
                                            <option key={d.id} value={d.id}>
                                                {d.title}
                                            </option>
                                        ))}
                                    </select>

                                    <button
                                        type="button"
                                        className="smeta-rooms__btn smeta-rooms__btn--small"
                                        onClick={() => setPanel({ oi: null, mode: null })}
                                    >
                                        Закрыть
                                    </button>
                                </div>

                                {!filteredDoorCatalogForRow(oi).length ? (
                                    <div className="smeta-rooms__door-panel-hint">
                                        Пока нет других дверей для связывания. Создай дверь на другой стене.
                                    </div>
                                ) : (
                                    <div className="smeta-rooms__door-panel-hint">
                                        Связь — это общий doorGroupId. Названия берутся из label каждой стороны (сделай понятные подписи).
                                    </div>
                                )}
                            </div>
                        ) : null}

                        {kind === "doors" && panelOpen(oi, "second") ? (
                            <div
                                className="smeta-rooms__door-panel"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                }}
                            >
                                <div className="smeta-rooms__door-panel-title">Добавить 2-ю сторону двери</div>

                                <div className="smeta-rooms__door-panel-row">
                                    <select
                                        className="smeta-rooms__select"
                                        defaultValue=""
                                        onChange={(e) => handleAddSecondSide(oi, e.target.value)}
                                    >
                                        <option value="" disabled>
                                            Выбрать стену...
                                        </option>
                                        {wallsCatalog
                                            .filter((w) => !(w.ri === selfRi && w.wi === selfWi))
                                            .map((w) => (
                                                <option key={`${w.ri}:${w.wi}`} value={`${w.ri}:${w.wi}`}>
                                                    {w.title}
                                                </option>
                                            ))}
                                    </select>

                                    <button
                                        type="button"
                                        className="smeta-rooms__btn smeta-rooms__btn--small"
                                        onClick={() => setPanel({ oi: null, mode: null })}
                                    >
                                        Закрыть
                                    </button>
                                </div>

                                <div className="smeta-rooms__door-panel-hint">
                                    Это создаст вторую запись двери на выбранной стене и свяжет обе стороны одним doorGroupId.
                                </div>
                            </div>
                        ) : null}
                    </div>
                );
            })}
        </div>
    );
}
